import { Button, Icons, Tooltip } from "@pankod/refine-antd";
import {
  useApiUrl,
  useCustomMutation,
  useNotification,
} from "@pankod/refine-core";
import { useRef, useState } from "react";

import {
  UPDATE_BUDGET_STATUS_LABEL,
  buildUpdateBudgetStatusPath,
  getUpdateBudgetStatusErrorMessage,
  getUpdateBudgetStatusSuccessMessage,
  hasValidControlNumber,
  unwrapUpdateBudgetStatusResponse,
} from "../utils/update-budget-status";

type UpdateBudgetStatusActionProps = {
  controlNumber?: string | number | null;
  onSuccess?: () => void;
};

export const UpdateBudgetStatusAction: React.FC<
  UpdateBudgetStatusActionProps
> = ({ controlNumber, onSuccess }) => {
  const apiUrl = useApiUrl();
  const notification = useNotification();
  const { mutate, isLoading } = useCustomMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const canExecute = hasValidControlNumber(controlNumber);
  const pending = isLoading || isSubmitting;
  const disabled = !canExecute || pending;

  const handleClick = () => {
    if (!canExecute || pending || submittingRef.current) {
      return;
    }

    const path = buildUpdateBudgetStatusPath(controlNumber as string | number);

    if (!path) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    mutate(
      {
        url: `${apiUrl}${path}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (response) => {
          const data = unwrapUpdateBudgetStatusResponse(response);

          submittingRef.current = false;
          setIsSubmitting(false);
          notification.open?.({
            type: "success",
            message: getUpdateBudgetStatusSuccessMessage(data),
          });
          onSuccess?.();
        },
        onError: () => {
          submittingRef.current = false;
          setIsSubmitting(false);
          notification.open?.({
            type: "error",
            message: getUpdateBudgetStatusErrorMessage(),
          });
        },
      },
    );
  };

  return (
    <Tooltip title={UPDATE_BUDGET_STATUS_LABEL}>
      <span className="update-budget-status-action-tooltip">
        <Button
          aria-label={UPDATE_BUDGET_STATUS_LABEL}
          className="update-budget-status-action"
          disabled={disabled}
          icon={!pending ? <Icons.ReloadOutlined /> : undefined}
          loading={pending}
          onClick={handleClick}
          size="small"
          title={UPDATE_BUDGET_STATUS_LABEL}
        />
      </span>
    </Tooltip>
  );
};
